/**
 * smsPromo
 * ------------------------------------------------------------------------------
 * SMS promo unlock (HR-276). A phone number submitted in any Klaviyo form, or
 * a visit with `?sms_promo=1`, unlocks the promo: we remember it in a cookie
 * and mirror it into the cart attribute the SMS promo Discount Function reads
 * (`attribute` in the config, `_sms_promo` by default).
 *
 * Logged-in customers with a phone on their account are unlocked as well when
 * the promo allows it (`unlockWithBuyerPhone`). The live cart is checked on page
 * load, on every Rebuy cart event and on every checkout click, and the attribute is
 * written (and read back) whenever the cart lacks it, so a fresh cart (e.g. after
 * checkout) picks the promo up again. See ensureCartAttribute().
 *
 * Modals — content and switches come from the metafield (`signup`, `success`):
 * - Signup, on collections the discount applies to (`collectionEligible`), for
 *   visitors who haven't unlocked the promo. `formType: "embed"` loads
 *   sections/sms-promo-popup.liquid (our modal around the Klaviyo form),
 *   `formType: "popup"` opens the Klaviyo popup form itself. `?sms_popup=1`
 *   skips the delay.
 * - Success (sections/sms-promo-success.liquid), right after a phone is
 *   submitted in any Klaviyo form, and once on those collections for visitors
 *   who already unlocked the promo.
 * Sections are fetched through the Section Rendering API and appended to
 * <body>, where Alpine picks up their components.
 *
 * Everything is a no-op unless the promo is live — see the shop metafield
 * `henry_rose.sms_promo`, exposed as `window.theme.smsPromo` by snippets/sms-promo-state.liquid.
 *
 * @see https://developers.klaviyo.com/en/docs/track_klaviyo_form_activity_using_javascript
 * @see https://developers.klaviyo.com/en/docs/how_to_custom_trigger_a_popup_or_flyout_form
 * @namespace smsPromo
 */

const COOKIE = 'hr_sms_promo';
const SUCCESS_SEEN_COOKIE = 'hr_sms_promo_success_seen';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const UNLOCKED_EVENT = 'sms-promo:unlocked';
const UNLOCKED_CLASS = 'sms-promo-unlocked';
const URL_PARAM = 'sms_promo';
const POPUP_URL_PARAM = 'sms_popup';
const SYNCING_CLASS = 'sms-promo-syncing';
const SYNC_TIMEOUT = 10000;
const CHECKOUT_SELECTOR = '.rebuy-cart__checkout-button, .hr-cart__checkout, .rebuy-cart__shop-pay-button, .hr-cart__shop-pay';
const POPUP_SECTION = 'sms-promo-popup';
const SUCCESS_SECTION = 'sms-promo-success';

// Set from `?sms_popup=1` in initSmsPromo(), which runs before Alpine initializes the popup.
let popupForced = false;
let successShown = false;

const state = {
    live: false,
    attribute: '_sms_promo',
    startsAt: null,
    endsAt: null,
    cartUnlocked: false,
    customerHasPhone: false,
    collectionEligible: false,
    signup: {enabled: false, formType: 'embed', klaviyoFormId: "", delay: 0},
    success: {enabled: false, showOnCollection: false},
    ...(window.theme?.smsPromo || {}),
};

function getCookie(name) {
    return document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${name}=`))
        ?.split('=')[1] ?? null;
}

function setCookie(name, value) {
    document.cookie = `${name}=${value}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
}

function getPhone(metaData) {
    return metaData?.$phone_number ?? metaData?.phone_number ?? null;
}

// Re-checked in JS because storefront pages can be served from cache after the dates pass.
export function isLive() {
    if (!state.live) {
        return false;
    }

    const now = Date.now();
    if (state.startsAt && now < Date.parse(state.startsAt)) {
        return false;
    }

    if (state.endsAt && now > Date.parse(state.endsAt)) {
        return false;
    }

    return true;
}

export function isUnlocked() {
    return !!getCookie(COOKIE) || !!state.customerHasPhone;
}

// The attribute has to be on the cart the visitor actually ends up with, and that cart can change
// under the page:
// - When there is no cart yet, the first `cart/update.js` creates one, and other scripts on the page
//   (EdgeTag, Rebuy) create theirs in parallel. The last `Set-Cookie` wins, so a write sent before
//   the visitor has a cart can land on a cart that's thrown away. We never create the cart: with no
//   `cart` cookie there is nothing to discount yet, and the cart events or the checkout guard below
//   pick the cart up once it exists.
// - A new cart can appear without a page load (an order placed in another tab, a bfcache restore).
// So every check reads the live cart (`/cart.js`) and every write is confirmed by reading it again.
const CART_COOKIE = 'cart';
const WRITE_ATTEMPTS = 3;
const RETRY_DELAY = 400;
// Lets the page's own cart writes (EdgeTag, Rebuy) settle before the first check on page load.
const LOAD_SETTLE_DELAY = 1500;

let ensuring = null;
let pendingSyncs = 0;
let replaying = false;

function setSyncing(on) {
    document.documentElement.classList.toggle(SYNCING_CLASS, on);
}

function shouldSync() {
    return isLive() && isUnlocked();
}

function hasCart() {
    return !!getCookie(CART_COOKIE);
}

function cartHasAttribute(cart) {
    return !!cart?.attributes?.[state.attribute];
}

function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchCart() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {cache: 'no-store'});

    if (!response.ok) {
        throw new Error('Network response was not ok');
    }

    return response.json();
}

async function writeAttribute() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            attributes: {
                [state.attribute]: '1',
            },
        }),
    });

    if (!response.ok) {
        throw new Error('Network response was not ok');
    }
}

// Resolves true once the live cart carries the attribute (or nothing needs it), false if it couldn't
// be written. Concurrent calls share one run. Checkout stays blocked (`sms-promo-syncing`) meanwhile.
function ensureCartAttribute() {
    if (!shouldSync() || !hasCart()) {
        if (!pendingSyncs) {
            setSyncing(false);
        }

        return Promise.resolve(true);
    }

    if (ensuring) {
        return ensuring;
    }

    pendingSyncs++;
    setSyncing(true);

    // Never leave checkout blocked behind a request that hangs.
    const failsafe = setTimeout(() => setSyncing(false), SYNC_TIMEOUT);

    ensuring = (async () => {
        let wrote = false;

        try {
            for (let attempt = 0; attempt < WRITE_ATTEMPTS; attempt++) {
                if (cartHasAttribute(await fetchCart())) {
                    state.cartUnlocked = true;

                    if (wrote) {
                        await window.hrSmartCart?.refreshCart?.();
                    }

                    return true;
                }

                if (attempt) {
                    await wait(RETRY_DELAY * attempt);
                }

                await writeAttribute();
                wrote = true;
            }

            state.cartUnlocked = cartHasAttribute(await fetchCart());

            if (state.cartUnlocked) {
                await window.hrSmartCart?.refreshCart?.();
            }

            return state.cartUnlocked;
        } catch (error) {
            state.cartUnlocked = false;

            return false;
        } finally {
            clearTimeout(failsafe);
            ensuring = null;
            pendingSyncs--;

            if (!pendingSyncs) {
                setSyncing(false);
            }
        }
    })();

    return ensuring;
}

// Rebuy reports every cart change with the cart in `event.detail.cart`. Its shape varies between
// events, so a cart we can't read is checked against `/cart.js` instead.
function onCartEvent(event) {
    if (!shouldSync()) {
        return;
    }

    const cart = event?.detail?.cart;
    const attributes = cart?.attributes ?? cart?.cart?.attributes;

    if (attributes && attributes[state.attribute]) {
        state.cartUnlocked = true;

        return;
    }

    state.cartUnlocked = false;
    ensureCartAttribute();
}

// Checkout always goes through one live check: the click is held, the attribute confirmed (written if
// the cart lost it), and then the same click or submit is replayed.
async function guardCheckout(event) {
    if (replaying || !shouldSync()) {
        return;
    }

    // A submit's target is the form, so check the button that submitted it.
    const element = event.type === 'submit' ? event.submitter : event.target;
    const trigger = element?.closest?.(CHECKOUT_SELECTOR);
    if (!trigger) {
        return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    // Checkout goes ahead either way once the check is done or times out.
    await Promise.race([ensureCartAttribute(), wait(SYNC_TIMEOUT)]);

    replaying = true;

    try {
        if (event.type === 'submit') {
            event.target.requestSubmit(event.submitter || undefined);
        } else {
            trigger.click();
        }
    } finally {
        replaying = false;
    }
}

// Appends a section rendered through the Section Rendering API. Sections render an empty
// wrapper when there's nothing to show (promo not live, modal disabled), so only a fragment
// with an Alpine component is appended.
async function loadSection(name) {
    try {
        const response = await fetch(`${window.location.pathname}?sections=${name}`);

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        const sections = await response.json();
        const template = document.createElement('template');
        template.innerHTML = sections?.[name] || '';

        if (!template.content.querySelector('[x-data]')) {
            return false;
        }

        document.body.append(template.content);
        return true;
    } catch (error) {
        return false;
    }
}

async function showSuccess() {
    if (successShown || !state.success?.enabled) {
        return;
    }

    successShown = true;
    setCookie(SUCCESS_SEEN_COOKIE, '1');
    await loadSection(SUCCESS_SECTION);
}

function onKlaviyoFormEvent(event) {
    const {type, metaData} = event?.detail || {};
    if (type !== 'submit' && type !== 'stepSubmit') {
        return;
    }

    const phone = getPhone(metaData);
    if (!phone) {
        return;
    }

    unlock();
    showSuccess();
}

function unlock() {
    if (!isLive()) {
        return;
    }

    setCookie(COOKIE, '1');
    document.documentElement.classList.add(UNLOCKED_CLASS);

    state.cartUnlocked = false;
    ensureCartAttribute();

    window.dispatchEvent(new CustomEvent(UNLOCKED_EVENT));
}

// Returns true when `?name=1` is present, and strips the param from the URL either way.
function consumeUrlParam(name) {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(name)) {
        return false;
    }

    const value = url.searchParams.get(name);
    url.searchParams.delete(name);
    window.history.replaceState(window.history.state, '', url.toString());

    return value === '1';
}

async function openSignup() {
    const {enabled, formType, klaviyoFormId, delay} = state.signup || {};
    if (!enabled || !klaviyoFormId) {
        return;
    }

    if (formType !== 'popup') {
        await loadSection(POPUP_SECTION);

        return;
    }

    setTimeout(() => {
        if (isUnlocked()) {
            return;
        }

        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(['openForm', klaviyoFormId]);
    }, popupForced ? 0 : (Number(delay) || 0) * 1000);
}

export function smsPromoPopup({delay = 0} = {}) {
    return {
        open: false,

        init() {
            if (!isLive() || isUnlocked()) {
                return;
            }

            window.addEventListener(UNLOCKED_EVENT, () => {
                this.open = false;
            });

            setTimeout(() => {
                if (!isUnlocked()) {
                    this.open = true;
                }
            }, popupForced ? 0 : delay * 1000);
        },

        close() {
            this.open = false;
        },
    };
}

export function smsPromoSuccess() {
    return {
        open: false,

        init() {
            this.open = true;
        },

        close() {
            this.open = false;
        },
    };
}

async function initSmsPromo() {
    if (window.__smsPromoInit) {
        return;
    }

    window.__smsPromoInit = true;

    // Consume both params even when the promo is off, so links never leave them in the URL.
    popupForced = consumeUrlParam(POPUP_URL_PARAM);
    const unlockRequested = consumeUrlParam(URL_PARAM);

    if (!isLive()) {
        setSyncing(false);

        return;
    }

    document.addEventListener('click', guardCheckout, true);
    document.addEventListener('submit', guardCheckout, true);
    document.addEventListener('rebuy:cart.ready', onCartEvent);
    document.addEventListener('rebuy:cart.change', onCartEvent);
    window.addEventListener('klaviyoForms', onKlaviyoFormEvent);

    // A page restored from the back/forward cache may be holding on to a cart that has since been replaced.
    window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
            ensureCartAttribute();
        }
    });

    if (unlockRequested) {
        unlock();
    }

    // The server-rendered state is right for the cart the page was rendered with. Without a cart there
    // is nothing to write yet: look again once the page's own scripts have created one.
    const hadCart = hasCart();

    if (state.cartUnlocked && hadCart) {
        setSyncing(false);
    } else {
        await ensureCartAttribute();
    }

    if (!hadCart) {
        setTimeout(ensureCartAttribute, LOAD_SETTLE_DELAY);
    }

    if (!state.collectionEligible) {
        return;
    }

    if (!isUnlocked()) {
        openSignup();
    } else if (state.success?.showOnCollection && !getCookie(SUCCESS_SEEN_COOKIE)) {
        showSuccess();
    }
}

initSmsPromo();
